import type {
  SitePortDeleteApiData,
  SitePortDeleteApiResponses,
  SitePortGetApiData,
  SitePortListApiData,
  SitePortPatchApiData
} from '@navio/nsdk';

import type { ApiInput } from '../../utils';
import { apiUrl, dataEndpoint, responseEndpoint } from '../defineResource';
import type {
  SitePortCreateApiDataWithStorage,
  SitePortCreateApiResponsesWithStorage,
  SitePortGetApiResponsesWithStorage,
  SitePortListApiResponsesWithStorage,
  SitePortPatchApiDataWithStorage,
  SitePortPatchApiResponsesWithStorage,
  SitePortRecordCreateWithStorage,
  SitePortRecordPatchWithStorage
} from './storageMappings';

export type { SitePortRecordListWithStorage } from './storageMappings';

type SitePortCreateInput = ApiInput<SitePortCreateApiDataWithStorage>;
type SitePortPatchInput = ApiInput<SitePortPatchApiDataWithStorage>;
/** Image upload keeps the generated path + body shape; storage mapping is not sent here. */
type SitePortImagePatchInput = ApiInput<SitePortPatchApiData>;

export const getSitePortUrls = {
  getPaginatedSitePorts: dataEndpoint<SitePortListApiData, SitePortListApiResponsesWithStorage>({
    getUrl: (queryParams) => apiUrl('site-ports/', queryParams)
  }),
  getSitePort: dataEndpoint<SitePortGetApiData, SitePortGetApiResponsesWithStorage>({
    getUrl: ({ sitePortId }) => apiUrl(`site-ports/${sitePortId}/`)
  }),
  postSitePort: responseEndpoint<SitePortCreateApiDataWithStorage, SitePortCreateApiResponsesWithStorage>({
    getUrl: () => apiUrl('site-ports/'),
    getBody: (data: SitePortCreateInput) => data
  }),
  patchSitePort: responseEndpoint<SitePortPatchApiDataWithStorage, SitePortPatchApiResponsesWithStorage>({
    getUrl: ({ sitePortId }: SitePortPatchInput) => apiUrl(`site-ports/${sitePortId}/`),
    getBody: ({ sitePortId: _, ...body }: SitePortPatchInput) => body
  }),
  patchSitePortImage: responseEndpoint<SitePortPatchApiData, SitePortPatchApiResponsesWithStorage>({
    getUrl: ({ sitePortId }: SitePortImagePatchInput) => apiUrl(`site-ports/${sitePortId}/`),
    getHeaders: () => ({
      'Content-Type': 'multipart/form-data'
    }),
    getBody: (data: SitePortImagePatchInput) => data,
    skipBodyTransformation: true
  }),
  deleteSitePort: dataEndpoint<SitePortDeleteApiData, SitePortDeleteApiResponses>({
    getUrl: ({ sitePortId }) => apiUrl(`site-ports/${sitePortId}/`)
  })
};

/** Build create/patch leaf-mapping body from selected ids (empty list clears). No storage areas. */
export const toSitePortStorageMappingWrite = ({
  storageUnitIds,
  positionIds,
  workstationIds
}: {
  storageUnitIds: number[];
  positionIds: number[];
  workstationIds: number[];
}): Pick<
  SitePortRecordCreateWithStorage & SitePortRecordPatchWithStorage,
  'storageUnits' | 'positions' | 'workstations'
> => ({
  storageUnits: { setStorageUnitIds: storageUnitIds },
  positions: { setPositionIds: positionIds },
  workstations: { setWorkstationIds: workstationIds }
});
